import { useState } from "react";

import type { ApiClient } from "../api/client";
import type { CalendarDay, Entry, Me } from "../api/types";
import { formatDayLabel } from "../lib/dates";
import { formatDuration } from "../lib/duration";
import { EntryForm } from "./EntryForm";

interface Props {
  api: ApiClient;
  me: Me;
  day: CalendarDay;
  entries: Entry[];
  targetUserId: string | null;
  onChanged: () => void | Promise<void>;
}

export function DayPanel({ api, me, day, entries, targetUserId, onChanged }: Props): JSX.Element {
  const [editing, setEditing] = useState<Entry | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Admins keep writing on a locked day; employees do not. The server enforces the same
  // rule, so this only decides whether the controls are worth showing.
  const canEdit = me.is_privileged || !day.locked;
  const dayTotal = entries.reduce((sum, entry) => sum + entry.minutes, 0);

  async function run(action: () => Promise<unknown>): Promise<void> {
    setError(null);
    try {
      await action();
      await onChanged();
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "Something went wrong.");
    }
  }

  async function toggleLock(): Promise<void> {
    if (!me.is_privileged) return;
    const userId = targetUserId ?? me.id;
    await run(() =>
      day.locked ? api.unlockDay(day.work_date, userId) : api.lockDay(day.work_date, userId),
    );
  }

  return (
    <aside className="day-panel card">
      <header className="day-panel-header">
        <div>
          <h2>{formatDayLabel(day.work_date)}</h2>
          <p className="muted small">
            {formatDuration(dayTotal)} across {entries.length}{" "}
            {entries.length === 1 ? "entry" : "entries"}
          </p>
        </div>
        {day.locked && (
          <span className="pill locked-pill">
            {day.is_override ? "Locked by admin" : "Locked"}
          </span>
        )}
      </header>

      {me.is_privileged && (
        <div className="admin-lock-controls">
          <button type="button" className="ghost small" onClick={() => void toggleLock()}>
            {day.locked ? "Unlock this day" : "Lock this day"}
          </button>
          {day.is_override && (
            <button
              type="button"
              className="ghost small"
              onClick={() =>
                void run(() => api.clearDayOverride(day.work_date, targetUserId ?? me.id))
              }
            >
              Use automatic rule
            </button>
          )}
        </div>
      )}

      {error && <p className="error">{error}</p>}

      <ul className="entry-list">
        {entries.length === 0 && <li className="muted">Nothing logged yet.</li>}
        {entries.map((entry) =>
          editing?.id === entry.id ? (
            <li key={entry.id} className="entry editing">
              <EntryForm
                entry={entry}
                onCancel={() => setEditing(null)}
                onSubmit={async (values) => {
                  await run(() =>
                    api.updateEntry(entry.id, {
                      task: values.task,
                      hours: values.hours,
                      minutes: values.minutes,
                      note: values.note || null,
                    }),
                  );
                  setEditing(null);
                }}
              />
            </li>
          ) : (
            <li key={entry.id} className="entry">
              <div className="entry-main">
                <span className="entry-task">{entry.task}</span>
                <span className="entry-duration">{entry.duration_label}</span>
              </div>
              {entry.note && <p className="entry-note muted small">{entry.note}</p>}
              {canEdit && (
                <div className="entry-actions">
                  <button type="button" className="link" onClick={() => setEditing(entry)}>
                    Edit
                  </button>
                  <button
                    type="button"
                    className="link danger"
                    disabled={busyId === entry.id}
                    onClick={() => {
                      setBusyId(entry.id);
                      void run(() => api.deleteEntry(entry.id)).finally(() => setBusyId(null));
                    }}
                  >
                    Delete
                  </button>
                </div>
              )}
            </li>
          ),
        )}
      </ul>

      {canEdit ? (
        !editing && (
          <EntryForm
            onSubmit={(values) =>
              run(() =>
                api.createEntry(
                  {
                    work_date: day.work_date,
                    task: values.task,
                    hours: values.hours,
                    minutes: values.minutes,
                    note: values.note || null,
                  },
                  targetUserId ?? undefined,
                ),
              )
            }
          />
        )
      ) : (
        <p className="muted small locked-note">
          This day locked 24 hours after it ended. Ask an administrator to unlock it if you
          need to make a correction.
        </p>
      )}
    </aside>
  );
}
